import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber, Select,
  DatePicker, Space, Row, Col, message,
  Popconfirm, Tooltip, Drawer, Descriptions, List
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SendOutlined,
  CheckCircleOutlined, FileTextOutlined, ShoppingCartOutlined,
  DollarOutlined, ClockCircleOutlined, TruckOutlined
} from '@ant-design/icons';
import { purchaseOrderService, clientVendorService } from '../services/api';
import dayjs from 'dayjs';
import '../styles/Phase5.css';

const { Option } = Select;
const { TextArea } = Input;

const PurchaseOrders = () => {
  const [loading, setLoading] = useState(true);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [receiptModalVisible, setReceiptModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [receiptForm] = Form.useForm();

  const fetchData = async () => {
    try {
      setLoading(true);
      const [posData, statsData, vendorsData] = await Promise.all([
        purchaseOrderService.getAll(),
        purchaseOrderService.getStats(),
        clientVendorService.getAll(),
      ]);
      setPurchaseOrders(posData);
      setStats(statsData);
      setVendors(vendorsData);
    } catch (error) {
      message.error('Failed to load purchase orders');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showAddModal = () => {
    setEditingPO(null);
    setLineItems([{ key: 1, description: '', quantity: 1, unit_price: 0 }]);
    form.resetFields();
    form.setFieldsValue({ order_date: dayjs(), currency: 'USD' });
    setIsModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditingPO(record);
    setLineItems(record.line_items?.map((item, index) => ({
      ...item,
      key: index + 1,
    })) || []);
    form.setFieldsValue({
      ...record,
      order_date: record.order_date ? dayjs(record.order_date) : null,
      expected_delivery_date: record.expected_delivery_date ? dayjs(record.expected_delivery_date) : null,
    });
    setIsModalVisible(true);
  };

  const showDetailDrawer = async (record) => {
    try {
      const fullPO = await purchaseOrderService.getById(record.id);
      setSelectedPO(fullPO);
      setDrawerVisible(true);
    } catch (error) {
      message.error('Failed to load PO details');
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingPO(null);
    setLineItems([]);
    form.resetFields();
  };

  const calculateTotals = () => {
    return lineItems.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0), 0);
  };

  const handleSubmit = async (values) => {
    try {
      const poData = {
        ...values,
        order_date: values.order_date?.toISOString(),
        expected_delivery_date: values.expected_delivery_date?.toISOString(),
        line_items: lineItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          sku: item.sku,
          unit_of_measure: item.unit_of_measure,
        })),
      };

      if (editingPO) {
        await purchaseOrderService.update(editingPO.id, poData);
        message.success('Purchase order updated successfully');
      } else {
        await purchaseOrderService.create(poData);
        message.success('Purchase order created successfully');
      }

      handleCancel();
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to save purchase order');
    }
  };

  const handleDelete = async (id) => {
    try {
      await purchaseOrderService.delete(id);
      message.success('Purchase order deleted');
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to delete purchase order');
    }
  };

  const handleSubmitPO = async (id) => {
    try {
      await purchaseOrderService.submit(id);
      message.success('Purchase order submitted for approval');
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to submit purchase order');
    }
  };

  const handleApprovePO = async (id) => {
    try {
      await purchaseOrderService.approve(id);
      message.success('Purchase order approved');
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to approve purchase order');
    }
  };

  const showReceiptModal = (po) => {
    setSelectedPO(po);
    receiptForm.resetFields();
    receiptForm.setFieldsValue({
      receipt_number: `REC-${Date.now()}`,
      receipt_date: dayjs(),
      line_items: po.line_items?.map(item => ({
        po_line_item_id: item.id,
        description: item.description,
        quantity_received: item.quantity - (item.quantity_received || 0),
        condition: 'good',
      })),
    });
    setReceiptModalVisible(true);
  };

  const handleReceiveGoods = async (values) => {
    try {
      const receiptData = {
        ...values,
        receipt_date: values.receipt_date?.toISOString(),
        purchase_order_id: selectedPO.id,
      };
      await purchaseOrderService.receive(selectedPO.id, receiptData);
      message.success('Goods receipt recorded');
      setReceiptModalVisible(false);
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to record receipt');
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, {
      key: lineItems.length + 1,
      description: '',
      quantity: 1,
      unit_price: 0,
    }]);
  };

  const removeLineItem = (key) => {
    setLineItems(lineItems.filter(item => item.key !== key));
  };

  const updateLineItem = (key, field, value) => {
    setLineItems(lineItems.map(item =>
      item.key === key ? { ...item, [field]: value } : item
    ));
  };

  const getStatusTagClass = (status) => {
    const classes = {
      draft: 'p5-tag--neutral',
      submitted: 'p5-tag--info',
      approved: 'p5-tag--success',
      received: 'p5-tag--success',
      closed: 'p5-tag--neutral',
      cancelled: 'p5-tag--danger',
    };
    return classes[status] || 'p5-tag--neutral';
  };

  const columns = [
    {
      title: 'PO Number',
      dataIndex: 'po_number',
      key: 'po_number',
      render: (text, record) => (
        <button
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--p5-font-mono)',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'var(--p5-navy-800)',
            padding: 0,
          }}
          onClick={() => showDetailDrawer(record)}
        >
          {text}
        </button>
      ),
    },
    {
      title: 'Vendor',
      key: 'vendor',
      render: (_, record) => {
        const vendorName = record.vendor?.name ||
          record.client_vendor?.vendor?.name ||
          'N/A';
        return <span className="p5-table-primary">{vendorName}</span>;
      },
    },
    {
      title: 'Order Date',
      dataIndex: 'order_date',
      key: 'order_date',
      render: (date) => (
        <span className="p5-table-secondary">
          {date ? dayjs(date).format('MMM D, YYYY') : '-'}
        </span>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount, record) => (
        <span className="p5-font-mono p5-table-primary">
          {record.currency || 'USD'} {amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span className={`p5-tag ${getStatusTagClass(status)}`}>
          <span className="p5-tag-dot" />
          {status?.toUpperCase()}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          {record.status === 'draft' && (
            <>
              <Tooltip title="Edit">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => showEditModal(record)}
                  style={{ color: 'var(--p5-slate-400)' }}
                />
              </Tooltip>
              <Tooltip title="Submit for Approval">
                <Button
                  icon={<SendOutlined />}
                  size="small"
                  className="p5-btn p5-btn--accent"
                  onClick={() => handleSubmitPO(record.id)}
                />
              </Tooltip>
              <Popconfirm title="Delete this PO?" onConfirm={() => handleDelete(record.id)}>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                  style={{ color: 'var(--p5-rose-500)' }}
                />
              </Popconfirm>
            </>
          )}
          {record.status === 'submitted' && (
            <Tooltip title="Approve">
              <Button
                icon={<CheckCircleOutlined />}
                size="small"
                className="p5-btn p5-btn--accent"
                onClick={() => handleApprovePO(record.id)}
              />
            </Tooltip>
          )}
          {record.status === 'approved' && (
            <Tooltip title="Receive Goods">
              <Button
                icon={<TruckOutlined />}
                size="small"
                className="p5-btn p5-btn--primary"
                onClick={() => showReceiptModal(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="p5-page">
      {/* Page Header */}
      <div className="p5-page-header p5-flex-between" style={{ alignItems: 'flex-start' }}>
        <div>
          <h1 className="p5-page-title">Purchase Orders</h1>
          <p className="p5-page-subtitle">
            Create, manage, and track purchase orders with vendor approvals
          </p>
        </div>
        <Button
          className="p5-btn p5-btn--accent"
          icon={<PlusOutlined />}
          onClick={showAddModal}
        >
          Create PO
        </Button>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="p5-stats-grid p5-stagger">
          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--amber">
              <FileTextOutlined />
            </div>
            <div className="p5-stat-label">Total POs</div>
            <div className="p5-stat-value">{stats.total_pos}</div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--teal">
              <DollarOutlined />
            </div>
            <div className="p5-stat-label">Total Value</div>
            <div className="p5-stat-value p5-stat-value--currency">
              {stats.total_amount?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--violet">
              <ClockCircleOutlined />
            </div>
            <div className="p5-stat-label">Pending Approval</div>
            <div className="p5-stat-value" style={{
              color: stats.pending_approval > 0 ? 'var(--p5-amber-500)' : undefined
            }}>
              {stats.pending_approval}
            </div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--sky">
              <TruckOutlined />
            </div>
            <div className="p5-stat-label">Pending Receipt</div>
            <div className="p5-stat-value">{stats.pending_receipt}</div>
          </div>
        </div>
      )}

      {/* Main Table */}
      <div className="p5-card p5-animate-fadeUp" style={{ animationDelay: '200ms', padding: 0 }}>
        <div className="p5-flex-between" style={{ padding: '20px 24px', borderBottom: '1px solid var(--p5-slate-100)' }}>
          <h3 className="p5-section-title" style={{ margin: 0 }}>
            <ShoppingCartOutlined /> Purchase Order List
          </h3>
        </div>
        <div className="p5-table-wrapper" style={{ border: 'none', boxShadow: 'none' }}>
          <Table
            columns={columns}
            dataSource={purchaseOrders}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        title={null}
        open={isModalVisible}
        onCancel={handleCancel}
        width={900}
        footer={null}
        className="p5-modal"
      >
        <div style={{
          background: 'var(--p5-navy-900)',
          margin: '-24px -24px 24px',
          padding: '24px',
          borderRadius: 'var(--p5-radius-lg) var(--p5-radius-lg) 0 0'
        }}>
          <h2 className="p5-font-display" style={{ fontSize: '1.5rem', color: '#fff', margin: 0 }}>
            {editingPO ? 'Edit Purchase Order' : 'Create Purchase Order'}
          </h2>
        </div>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="po_number" label={<span className="p5-label">PO Number</span>} rules={[{ required: true }]}>
                <Input placeholder="PO-2024-001" size="large" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="client_vendor_id" label={<span className="p5-label">Vendor</span>} rules={[{ required: true }]}>
                <Select placeholder="Select vendor" showSearch optionFilterProp="children" size="large">
                  {vendors.map(v => (
                    <Option key={v.id} value={v.id}>{v.vendor?.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currency" label={<span className="p5-label">Currency</span>}>
                <Select size="large">
                  <Option value="USD">USD</Option>
                  <Option value="EUR">EUR</Option>
                  <Option value="GBP">GBP</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="order_date" label={<span className="p5-label">Order Date</span>}>
                <DatePicker style={{ width: '100%' }} size="large" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expected_delivery_date" label={<span className="p5-label">Expected Delivery</span>}>
                <DatePicker style={{ width: '100%' }} size="large" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{
            marginTop: '24px',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '1px solid var(--p5-slate-200)'
          }}>
            <span style={{ fontWeight: 600, color: 'var(--p5-navy-800)', fontSize: '0.9375rem' }}>Line Items</span>
          </div>

          {lineItems.map((item) => (
            <Row gutter={8} key={item.key} style={{ marginBottom: 12 }}>
              <Col span={9}>
                <Input
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) => updateLineItem(item.key, 'description', e.target.value)}
                  size="large"
                />
              </Col>
              <Col span={4}>
                <InputNumber
                  placeholder="Qty"
                  min={1}
                  value={item.quantity}
                  onChange={(val) => updateLineItem(item.key, 'quantity', val)}
                  style={{ width: '100%' }}
                  size="large"
                />
              </Col>
              <Col span={5}>
                <InputNumber
                  placeholder="Unit Price"
                  min={0}
                  precision={2}
                  value={item.unit_price}
                  onChange={(val) => updateLineItem(item.key, 'unit_price', val)}
                  style={{ width: '100%' }}
                  size="large"
                  prefix="$"
                />
              </Col>
              <Col span={5}>
                <Input
                  disabled
                  value={`$${((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}`}
                  size="large"
                  style={{ fontFamily: 'var(--p5-font-mono)', fontWeight: 600 }}
                />
              </Col>
              <Col span={1}>
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => removeLineItem(item.key)}
                  disabled={lineItems.length === 1}
                  size="large"
                />
              </Col>
            </Row>
          ))}

          <Button type="dashed" onClick={addLineItem} icon={<PlusOutlined />} style={{ marginBottom: 24 }}>
            Add Line Item
          </Button>

          <Row justify="end" style={{ marginBottom: 24 }}>
            <div style={{
              background: 'var(--p5-navy-900)',
              padding: '16px 24px',
              borderRadius: 'var(--p5-radius-md)'
            }}>
              <span className="p5-label" style={{ color: 'var(--p5-slate-300)', marginRight: '16px' }}>Total</span>
              <span className="p5-font-display" style={{ fontSize: '1.75rem', color: 'var(--p5-amber-400)' }}>
                ${calculateTotals().toFixed(2)}
              </span>
            </div>
          </Row>

          <Form.Item name="notes" label={<span className="p5-label">Notes</span>}>
            <TextArea rows={2} size="large" />
          </Form.Item>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button className="p5-btn p5-btn--ghost" onClick={handleCancel}>Cancel</Button>
            <Button className="p5-btn p5-btn--accent" htmlType="submit">
              {editingPO ? 'Update' : 'Create'} PO
            </Button>
          </Space>
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Drawer
        title={null}
        placement="right"
        width={600}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        className="p5-drawer"
        headerStyle={{ display: 'none' }}
      >
        {selectedPO && (
          <>
            {/* Drawer Header */}
            <div style={{
              background: 'var(--p5-navy-900)',
              margin: '-24px -24px 24px',
              padding: '32px 24px',
            }}>
              <div className="p5-label" style={{ color: 'var(--p5-slate-300)', marginBottom: '8px' }}>
                Purchase Order
              </div>
              <h2 className="p5-font-display" style={{ fontSize: '1.75rem', color: '#fff', margin: 0 }}>
                {selectedPO.po_number}
              </h2>
              <div style={{ marginTop: '16px' }}>
                <span className={`p5-tag ${getStatusTagClass(selectedPO.status)}`}>
                  <span className="p5-tag-dot" />
                  {selectedPO.status?.toUpperCase()}
                </span>
              </div>
            </div>

            {/* Details */}
            <div style={{
              background: 'var(--p5-slate-100)',
              borderRadius: 'var(--p5-radius-md)',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div className="p5-label">Vendor</div>
                  <div style={{ color: 'var(--p5-navy-800)', fontWeight: 500 }}>
                    {selectedPO.vendor?.name || selectedPO.client_vendor?.vendor?.name}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="p5-label">Order Date</div>
                  <div style={{ color: 'var(--p5-navy-800)' }}>
                    {selectedPO.order_date ? dayjs(selectedPO.order_date).format('MMM D, YYYY') : '-'}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="p5-label">Expected Delivery</div>
                  <div style={{ color: 'var(--p5-navy-800)' }}>
                    {selectedPO.expected_delivery_date ? dayjs(selectedPO.expected_delivery_date).format('MMM D, YYYY') : '-'}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="p5-label">Total</div>
                  <div className="p5-font-mono" style={{ color: 'var(--p5-navy-800)', fontWeight: 600 }}>
                    {selectedPO.currency} {selectedPO.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Line Items */}
            <h3 className="p5-section-title" style={{ fontSize: '1rem' }}>Line Items</h3>
            <List
              dataSource={selectedPO.line_items}
              renderItem={(item) => (
                <List.Item style={{
                  background: '#fff',
                  borderRadius: 'var(--p5-radius-md)',
                  padding: '16px',
                  marginBottom: '8px',
                  border: '1px solid var(--p5-slate-200)'
                }}>
                  <div style={{ width: '100%' }}>
                    <div className="p5-flex-between" style={{ marginBottom: '8px' }}>
                      <span style={{ fontWeight: 600, color: 'var(--p5-navy-800)' }}>{item.description}</span>
                      <span className="p5-font-mono" style={{ fontWeight: 600, color: 'var(--p5-navy-800)' }}>
                        ${item.total_price?.toFixed(2)}
                      </span>
                    </div>
                    <div className="p5-flex p5-gap-lg">
                      <span className="p5-text-slate">
                        Qty: <span className="p5-font-mono">{item.quantity}</span>
                      </span>
                      <span className="p5-text-slate">
                        @ <span className="p5-font-mono">${item.unit_price?.toFixed(2)}</span>
                      </span>
                      {item.quantity_received > 0 && (
                        <span className="p5-tag p5-tag--success">
                          Received: {item.quantity_received}
                        </span>
                      )}
                    </div>
                  </div>
                </List.Item>
              )}
            />

            {selectedPO.notes && (
              <>
                <h3 className="p5-section-title" style={{ fontSize: '1rem', marginTop: '24px' }}>Notes</h3>
                <p style={{ color: 'var(--p5-navy-700)', lineHeight: 1.6 }}>{selectedPO.notes}</p>
              </>
            )}
          </>
        )}
      </Drawer>

      {/* Receipt Modal */}
      <Modal
        title={null}
        open={receiptModalVisible}
        onCancel={() => setReceiptModalVisible(false)}
        footer={null}
        width={700}
        className="p5-modal"
      >
        <div style={{
          background: 'var(--p5-navy-900)',
          margin: '-24px -24px 24px',
          padding: '24px',
          borderRadius: 'var(--p5-radius-lg) var(--p5-radius-lg) 0 0'
        }}>
          <h2 className="p5-font-display" style={{ fontSize: '1.5rem', color: '#fff', margin: 0 }}>
            Receive Goods - {selectedPO?.po_number}
          </h2>
        </div>
        <Form form={receiptForm} layout="vertical" onFinish={handleReceiveGoods}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="receipt_number" label={<span className="p5-label">Receipt Number</span>} rules={[{ required: true }]}>
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="receipt_date" label={<span className="p5-label">Receipt Date</span>} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="delivery_note_number" label={<span className="p5-label">Delivery Note #</span>}>
            <Input size="large" />
          </Form.Item>

          <div style={{
            marginTop: '24px',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '1px solid var(--p5-slate-200)'
          }}>
            <span style={{ fontWeight: 600, color: 'var(--p5-navy-800)', fontSize: '0.9375rem' }}>Items Received</span>
          </div>

          <Form.List name="line_items">
            {(fields) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={8} key={key} style={{ marginBottom: 12 }}>
                    <Col span={10}>
                      <Form.Item {...restField} name={[name, 'description']} noStyle>
                        <Input disabled size="large" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item {...restField} name={[name, 'quantity_received']} noStyle>
                        <InputNumber min={0} placeholder="Qty Received" style={{ width: '100%' }} size="large" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item {...restField} name={[name, 'condition']} noStyle>
                        <Select style={{ width: '100%' }} size="large">
                          <Option value="good">Good</Option>
                          <Option value="damaged">Damaged</Option>
                          <Option value="partial">Partial</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Form.Item {...restField} name={[name, 'po_line_item_id']} hidden>
                      <Input />
                    </Form.Item>
                  </Row>
                ))}
              </>
            )}
          </Form.List>

          <Form.Item name="notes" label={<span className="p5-label">Notes</span>}>
            <TextArea rows={2} size="large" />
          </Form.Item>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button className="p5-btn p5-btn--ghost" onClick={() => setReceiptModalVisible(false)}>Cancel</Button>
            <Button className="p5-btn p5-btn--accent" htmlType="submit">Record Receipt</Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default PurchaseOrders;
